import { Navigate, Outlet } from "react-router-dom";
import { useAuth } from "@/core/hooks/useAuth";

/**
 * Modelo de dois níveis do DA-002 (02-Arquitetura / 03-Núcleo):
 *   "admin-plataforma"    -> Nível 1: Perfil Administrador (eh_admin_plataforma()).
 *   "gerencia-biblioteca" -> Nível 2: Função Ministerial no Ministério
 *                            Biblioteca com pode_gerenciar_modulo (gerencia_modulo('biblioteca')).
 */
export type NivelAcesso =
  | "admin-plataforma"
  | "gerencia-biblioteca"
  | "gerencia-eventos"
  | "gerencia-reservas";

interface ProtectedRouteProps {
  /**
   * Se informado, o usuário precisa ter AO MENOS UM dos níveis listados
   * (ex.: a rota "/admin" é acessível por qualquer um dos dois níveis; já
   * "/admin/usuarios" exige especificamente "admin-plataforma").
   */
  niveisPermitidos?: NivelAcesso[];
  /**
   * Caso especial da rota "/usuario": exige que o usuário NÃO tenha nenhum
   * dos dois níveis administrativos (preserva o comportamento anterior, em
   * que um admin não acessa "/usuario" diretamente pela URL — ele usa
   * "/admin/visualizar-como-usuario" para isso).
   */
  apenasSemAcessoAdministrativo?: boolean;
  /**
   * SEG-02 (auditoria 20/07/2026): código de `modulos_visiveis()` que a
   * pessoa precisa ter no array `modulosVisiveis` para acessar a rota — sem
   * isso, "/eventos" e "/reservas" abriam por URL direta mesmo com o módulo
   * desativado ou sem visibilidade para a pessoa (a RLS não bastava sozinha:
   * a tela renderizava vazia em vez de redirecionar). A RLS (migration 0032)
   * agora também trava de verdade quando o módulo está desativado — este
   * guard é a camada de UI correspondente.
   */
  moduloRequerido?: string;
}

/**
 * Protege um conjunto de rotas (via <Outlet />):
 * - Se não autenticado -> redireciona para /login.
 * - Se autenticado mas sem os dados do Núcleo carregados ainda -> mostra estado de carregamento.
 * - Se `niveisPermitidos` for passado e o usuário não tiver nenhum deles -> redireciona para "/".
 * - Se `apenasSemAcessoAdministrativo` for passado e o usuário tiver algum nível administrativo -> redireciona para "/".
 */
export function ProtectedRoute({
  niveisPermitidos,
  apenasSemAcessoAdministrativo,
  moduloRequerido,
}: ProtectedRouteProps) {
  const {
    estaAutenticado,
    ehAdminPlataforma,
    podeGerenciarBiblioteca,
    podeGerenciarEventos,
    podeGerenciarReservas,
    modulosVisiveis,
    carregando,
    erroCarregamento,
    tentarNovamente,
  } = useAuth();

  // BUG-08 (auditoria 20/07/2026): se o bootstrap travou/falhou, oferece
  // "Tentar novamente" em vez de deixar a pessoa presa num spinner que
  // nunca sai — antes disso, a única saída era um F5 manual.
  if (erroCarregamento) {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-3 bg-bg px-6 text-center">
        <p className="text-sm text-muted">{erroCarregamento}</p>
        <button
          type="button"
          onClick={() => void tentarNovamente()}
          className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-on-primary transition-opacity hover:opacity-90"
        >
          Tentar novamente
        </button>
      </div>
    );
  }

  if (carregando) {
    return (
      <div className="flex h-screen items-center justify-center bg-bg">
        <p className="text-sm text-muted">Carregando...</p>
      </div>
    );
  }

  if (!estaAutenticado) {
    return <Navigate to="/login" replace />;
  }

  if (niveisPermitidos) {
    const temAlgumNivel =
      (niveisPermitidos.includes("admin-plataforma") && ehAdminPlataforma) ||
      (niveisPermitidos.includes("gerencia-biblioteca") && podeGerenciarBiblioteca) ||
      (niveisPermitidos.includes("gerencia-eventos") && podeGerenciarEventos) ||
      (niveisPermitidos.includes("gerencia-reservas") && podeGerenciarReservas);
    if (!temAlgumNivel) {
      return <Navigate to="/" replace />;
    }
  }

  if (apenasSemAcessoAdministrativo && (ehAdminPlataforma || podeGerenciarBiblioteca)) {
    return <Navigate to="/" replace />;
  }

  // SEG-02: módulo desativado ou sem visibilidade para a pessoa — a rota não
  // abre mais só porque a pessoa está autenticada. Admin sempre passa (ele
  // "vê tudo" por definição de produto), assim como quem gerencia o módulo.
  if (
    moduloRequerido &&
    !ehAdminPlataforma &&
    !modulosVisiveis.includes(moduloRequerido) &&
    !(moduloRequerido === "eventos" && podeGerenciarEventos) &&
    !(moduloRequerido === "reservas" && podeGerenciarReservas)
  ) {
    return <Navigate to="/modulos" replace />;
  }

  return <Outlet />;
}
